import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, Trophy, Wallet as WalletIcon, User, ScanLine, Users, Bell, Shield, LogOut, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common";

const navItems = [
  { to: "/", icon: Home, label: "Home", testid: "nav-home" },
  { to: "/leaderboard", icon: Trophy, label: "Ranks", testid: "nav-leaderboard" },
  { to: "/scan", icon: ScanLine, label: "Scan", testid: "nav-scan", center: true },
  { to: "/wallet", icon: WalletIcon, label: "Wallet", testid: "nav-wallet" },
  { to: "/profile", icon: User, label: "Profile", testid: "nav-profile" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (active) setUnread(data.filter((n) => !n.read).length);
      } catch {}
    };
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
  }, [location.pathname]);

  const isStaff = user && user.role && user.role !== "member";

  return (
    <div className="jx-bg jx-grid min-h-screen relative">
      {/* Top bar */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="brand-logo">
            <img src="/logo.png" alt="Jixxeo" className="w-9 h-9 rounded-xl object-cover" />
            <span className="font-display text-base text-jixxeo-green hidden sm:block">JIXXEO</span>
          </button>
          <div className="flex items-center gap-1.5">
            <button onClick={() => navigate("/community")} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white" data-testid="nav-community"><Users className="w-5 h-5" /></button>
            <button onClick={() => navigate("/search")} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white" data-testid="nav-search"><Search className="w-5 h-5" /></button>
            <button onClick={() => navigate("/notifications")} className="relative p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-white" data-testid="nav-notifications">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span className="absolute top-1 right-1 bg-jixxeo-danger text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{unread}</span>}
            </button>
            {isStaff && (
              <button onClick={() => navigate("/admin")} className="p-2 rounded-full hover:bg-white/10 text-jixxeo-gold" data-testid="nav-admin"><Shield className="w-5 h-5" /></button>
            )}
            <button onClick={() => { logout(); navigate("/login"); }} className="p-2 rounded-full hover:bg-white/10 text-muted-foreground hover:text-jixxeo-danger" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 pt-5 pb-28">{children}</main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-around">
          {navItems.map((it) => {
            const Icon = it.icon;
            if (it.center) {
              return (
                <NavLink key={it.to} to={it.to} data-testid={it.testid} className="-mt-8">
                  {({ isActive }) => (
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center bg-jixxeo-green neon-green pulse-glow border-4 border-background ${isActive ? "ring-2 ring-jixxeo-green" : ""}`}>
                      <Icon className="w-7 h-7 text-black" strokeWidth={2.5} />
                    </div>
                  )}
                </NavLink>
              );
            }
            return (
              <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={it.testid} className="flex flex-col items-center gap-0.5 w-14">
                {({ isActive }) => (
                  <>
                    <Icon className={`w-6 h-6 ${isActive ? "text-jixxeo-green" : "text-muted-foreground"}`} />
                    <span className={`text-[10px] ${isActive ? "text-jixxeo-green font-bold" : "text-muted-foreground"}`}>{it.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
